import { isRecord } from "../helpers/isRecord";
import { PermissionDeniedReason } from "../types/helpers/permissions";
import { AccountPermission } from "../types/models/AccountAccessBase";
import { Problem, ProblemWithDetail } from "./Problem";
import { ProblemCode } from "./ProblemCode";
import {
  problemMap,
  ProblemWithData,
  ProblemDataFor,
  ProblemForCode,
  ProblemStatusFor,
} from "./problemMap";
import { Resource } from "./problems/ResourceNotFoundProblem";
import { ValidationErrorData } from "./problems/ValidationProblem";

/**
 * A structured error for all failures in the backend REST API which should be
 * shared with the frontend.
 *
 * Throw this anywhere in the backend `express-app` during request handling.
 * The Express error middleware will serialise it into a standard
 * {@link Problem} error response.
 *
 * In the frontend `react-app` project, this error response is detected and then
 * thrown as a `SylaApiError`.
 *
 * **Important**: All information in this error will be exposed to the F/E, don't
 * include excessive internal information, passwords or other sensitive data.
 *
 * If the result of a request is an expected outcome, then don't throw an error.
 * Prefer to return a success response instead.
 *
 * If you don't want the error to be exposed outside the B/E, then don't use
 * `SylaApiError`, just throw `Error` instead. This will be caught in the
 * express error middleware and mapped to a `SylaApiError` with
 * `ProblemCode.InternalServerError`, and no additional data.
 *
 * How to choose which type of Error to throw?
 * - `SylaApiError`: If you need to communicate an error in the B/E to the F/E.
 * - `Error`: If you need to throw an error in the B/E logic, but not have it communicated to the F/E.
 * - `N/A`: If it's an expected outcome, prefer to send a structured success response for the F/E instead of an error.
 */
export class SylaApiError<C extends ProblemCode = ProblemCode> extends Error {
  code: C;
  status: ProblemStatusFor<C>;
  detail?: string;
  data: ProblemDataFor<C>;

  /**
   * Create a standard SylaApiError
   *
   * @param code - The error code from the API.
   * @param info - Optional Problem field data.
   */
  constructor(code: C, info?: ProblemWithDetail & ProblemWithData<C>) {
    super(info?.detail || code);
    this.name = "SylaApiError";
    this.code = code;

    // always use the predefined `status`
    this.status = problemMap[code].status;

    // fallback to use the predefined detail message where available
    const detail = info?.detail || problemMap[code].detail;
    if (detail) {
      this.detail = detail;
    }

    if (info && "data" in info) {
      this.data = info?.data;
    }
  }

  // static factories for some common errors are provided for convenience

  static AccountFeatureAccessDenied(deniedReason?: PermissionDeniedReason) {
    return new SylaApiError(ProblemCode.AccountFeatureAccessDenied, {
      data: deniedReason ?? {},
    });
  }

  static ValidationFailed(problems: ValidationErrorData[]) {
    return new SylaApiError(ProblemCode.ValidationFailed, {
      data: {
        problems,
      },
    });
  }

  static UserAccountPermissionDenied(
    permission: AccountPermission,
    info?: ProblemWithDetail,
  ) {
    const buildInfo: ProblemWithDetail &
      ProblemWithData<ProblemCode.UserAccountPermissionDenied> = {
      data: {
        permission,
      },
      ...info,
    };
    if (!info?.detail) {
      buildInfo.detail = `User does not have ${permission} permission on the account.`;
    }
    return new SylaApiError(ProblemCode.UserAccountPermissionDenied, buildInfo);
  }

  static ResourceNotFound(resource: Resource, info?: ProblemWithDetail) {
    const buildInfo: ProblemWithDetail &
      ProblemWithData<ProblemCode.ResourceNotFound> = {
      data: {
        resource,
      },
      ...info,
    };
    if (!info?.detail) {
      buildInfo.detail = `${resource} was not found.`;
    }
    return new SylaApiError(ProblemCode.ResourceNotFound, buildInfo);
  }

  /**
   * Serialise the error to a plain problem object.
   */
  toProblem(): Problem {
    return {
      code: this.code,
      status: this.status,
      ...(this.detail !== undefined && { detail: this.detail }),
      ...(this.data !== undefined && { data: this.data }),
    };
  }

  /**
   * Validate an error response (sent from the backend server).
   */
  static isProblem(problem: unknown): problem is ProblemForCode<ProblemCode> {
    return (
      isRecord(problem) &&
      typeof problem.code === "string" &&
      typeof problem.status === "number" &&
      (problem.detail === undefined || typeof problem.detail === "string") &&
      (problem.data === undefined || isRecord(problem.data))
    );
  }
}

export function isSylaApiError(error: unknown): error is SylaApiError {
  return error instanceof SylaApiError;
}

export function isSylaApiErrorCode<C extends ProblemCode>(
  error: unknown,
  code: C,
): error is SylaApiError<C> {
  return isSylaApiError(error) && error.code === code;
}
